import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * PrivateChatButton - Botão para usuário solicitar chat privado com criador
 */
export const PrivateChatButton = ({ creatorId, creatorName, onSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingRequest, setExistingRequest] = useState(null);

  const checkExistingRequest = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/chat/private/requests?token=${token}&role=user`);
      const existing = response.data.requests?.find(r => r.creator_id === creatorId);
      setExistingRequest(existing);
    } catch (error) {
      console.error('Error checking existing request:', error);
    }
  }, [creatorId]);

  useEffect(() => {
    checkExistingRequest();
  }, [checkExistingRequest]);

  const requestPrivateChat = async () => {
    if (!message.trim()) {
      toast.error('Digite uma mensagem inicial');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('token', token);
      formData.append('creator_id', creatorId);
      formData.append('message', message);

      const response = await axios.post(`${API}/api/chat/private/request`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.already_active) {
        toast.success('Chat privado já está ativo!');
        onSuccess?.();
      } else if (response.data.awaiting_payment) {
        toast.info('Aguardando pagamento');
        setExistingRequest(response.data.chat_request);
      } else {
        toast.success('Solicitação enviada! Aguarde o criador definir o preço.');
        setExistingRequest(response.data.chat_request);
      }
      
      setShowModal(false);
      setMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar solicitação');
    } finally {
      setLoading(false);
    }
  };

  // Se já tem chat ativo
  if (existingRequest?.status === 'active') {
    return (
      <button
        onClick={onSuccess}
        className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-xl font-semibold transition-all flex items-center gap-2"
      >
        💎 Chat Ativo
      </button>
    );
  }

  // Se aguardando pagamento
  if (existingRequest?.status === 'priced') {
    return (
      <PrivateChatPayment 
        chatRequest={existingRequest} 
        onSuccess={() => {
          checkExistingRequest();
          onSuccess?.();
        }} 
      />
    );
  }

  // Se aguardando preço
  if (existingRequest?.status === 'pending') {
    return (
      <button
        disabled
        className="px-4 py-2 bg-yellow-600/50 text-white rounded-xl font-semibold cursor-not-allowed flex items-center gap-2"
      >
        ⏳ Aguardando Preço
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-semibold transition-all flex items-center gap-2"
      >
        💎 Chat Privado
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] rounded-2xl max-w-md w-full p-6 border border-[#C79F5E]/20">
            <h2 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
              💎 Solicitar Chat Privado
            </h2>
            
            <p className="text-gray-400 mb-4">
              Envie uma mensagem para <span className="text-[#C79F5E] font-semibold">{creatorName}</span>.
              Após sua solicitação, o criador definirá um preço para liberar o chat.
            </p>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Escreva sua mensagem inicial..."
              className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder-gray-500 resize-none h-32 focus:border-[#C79F5E] outline-none mb-4"
              maxLength={1000}
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-semibold transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={requestPrivateChat}
                disabled={loading || !message.trim()}
                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
              >
                {loading ? 'Enviando...' : 'Enviar Solicitação'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * PrivateChatPayment - Componente para pagar pelo chat privado via PIX
 */
export const PrivateChatPayment = ({ chatRequest, onSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [checking, setChecking] = useState(false);

  const initiatePayment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('token', token);
      formData.append('chat_request_id', chatRequest.id);

      const response = await axios.post(`${API}/api/chat/private/pay`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPixData(response.data);
      setShowModal(true);
      
      // Iniciar polling para verificar pagamento
      startPaymentPolling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const startPaymentPolling = () => {
    const interval = setInterval(async () => {
      try {
        setChecking(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/api/chat/private/check-payment/${chatRequest.id}?token=${token}`
        );

        if (response.data.status === 'active') {
          clearInterval(interval);
          toast.success('🎉 Pagamento aprovado! Chat liberado!');
          setShowModal(false);
          onSuccess?.();
        }
      } catch (error) {
        console.error('Error checking payment:', error);
      } finally {
        setChecking(false);
      }
    }, 3000);

    // Parar após 10 minutos
    setTimeout(() => clearInterval(interval), 600000);
  };

  const copyPixCode = () => {
    if (pixData?.qr_code) {
      navigator.clipboard.writeText(pixData.qr_code);
      toast.success('Código PIX copiado!');
    }
  };

  return (
    <>
      <button
        onClick={initiatePayment}
        disabled={loading}
        className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white rounded-xl font-semibold transition-all flex items-center gap-2"
      >
        {loading ? '⏳' : '💳'} Pagar R${chatRequest.price?.toFixed(2)}
      </button>

      {/* Modal de Pagamento PIX */}
      {showModal && pixData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] rounded-2xl max-w-md w-full p-6 border border-[#C79F5E]/20">
            <h2 className="text-xl font-bold mb-4 text-white text-center">
              💚 Pagar via PIX
            </h2>
            
            <p className="text-center text-2xl font-bold text-[#C79F5E] mb-4">
              R$ {chatRequest.price?.toFixed(2)}
            </p>

            {/* QR Code */}
            {pixData.qr_code_base64 && (
              <div className="bg-white p-4 rounded-xl mb-4 flex justify-center">
                <img
                  src={`data:image/png;base64,${pixData.qr_code_base64}`}
                  alt="QR Code PIX"
                  className="w-48 h-48"
                />
              </div>
            )}

            <button
              onClick={copyPixCode}
              className="w-full py-3 bg-white/10 hover:bg-white/20 rounded-xl font-semibold transition-all mb-4 flex items-center justify-center gap-2"
            >
              📋 Copiar Código PIX
            </button>

            <p className="text-center text-sm text-gray-400 mb-4">
              {checking ? '🔄 Verificando pagamento...' : 'Escaneie o QR Code ou copie o código'}
            </p>

            <button
              onClick={() => setShowModal(false)}
              className="w-full py-3 bg-white/10 hover:bg-white/20 rounded-xl font-semibold transition-all"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * UserBroadcastFeed - Feed de anúncios/broadcasts para usuários
 */
export const UserBroadcastFeed = ({ creatorId }) => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBroadcasts = async () => {
      try {
        const url = creatorId 
          ? `${API}/api/chat/broadcasts?creator_id=${creatorId}&limit=20`
          : `${API}/api/chat/broadcasts?limit=50`;
        
        const response = await axios.get(url);
        setBroadcasts(response.data || []);
      } catch (error) {
        console.error('Error loading broadcasts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBroadcasts();
  }, [creatorId]);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white/5 rounded-xl h-32" />
        ))}
      </div>
    );
  }

  if (broadcasts.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p className="text-4xl mb-2">📢</p>
        <p>Nenhum anúncio ainda</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {broadcasts.map((broadcast) => (
        <div key={broadcast.id} className="bg-[#1A1A1A] rounded-xl p-4 border border-white/10">
          {/* Header com criador */}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4D1A4A] to-[#C79F5E] flex items-center justify-center overflow-hidden">
              {broadcast.creator_picture ? (
                <img src={getImageUrl(broadcast.creator_picture)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-bold">{broadcast.creator_name?.charAt(0)}</span>
              )}
            </div>
            <div>
              <p className="font-semibold text-white">{broadcast.creator_name}</p>
              <p className="text-xs text-gray-500">
                {new Date(broadcast.created_at).toLocaleString('pt-BR')}
              </p>
            </div>
            <span className="ml-auto text-xs bg-[#C79F5E]/20 text-[#C79F5E] px-2 py-1 rounded-full">
              📢 Anúncio
            </span>
          </div>

          {/* Conteúdo */}
          <p className="text-white mb-3">{broadcast.content}</p>

          {/* Imagem */}
          {broadcast.image_url && (
            <img
              src={getImageUrl(broadcast.image_url)}
              alt=""
              className="rounded-xl max-h-64 w-full object-cover mb-3"
            />
          )}

          {/* Link para post */}
          {broadcast.post_data && (
            <a
              href={`/post/${broadcast.post_data.id}`}
              className="block bg-white/5 hover:bg-white/10 rounded-xl p-3 transition-all"
            >
              <p className="text-sm text-[#C79F5E]">📌 Ver Post Relacionado</p>
              {broadcast.post_data.title && (
                <p className="text-white truncate">{broadcast.post_data.title}</p>
              )}
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

export default { PrivateChatButton, PrivateChatPayment, UserBroadcastFeed };
